import { Injectable } from '@angular/core';
import { IndexedService, logger } from '@damofire/data-commons';
import { PictureSyncRiskFilter, Preference } from '@damofire/data-stas';
import { WAREHOUSE_SYNC_STEP_CLEAN, WAREHOUSE_SYNC_STEP_DOWNLOAD, WAREHOUSE_SYNC_STEP_PUSH, WarehouseSyncStep, WarehouseSyncStepTask } from '../executors';
import { WarehouseCleanAssetsSyncStepTask, WarehouseDownloadAssetsSyncStepTask, WarehousePushAssetsSyncStepTask } from './assets.steps';
import { WarehouseDownloadConfigsSyncStepTask } from './configs.steps';
import { WarehouseCleanInspectionsSyncStepTask, WarehouseDownloadInspectionsSyncStepTask, WarehousePushInspectionsSyncStepTask } from './inspections.steps';
import {
  WarehouseCleanIssuesMediasFilesSyncStepTask,
  WarehouseDownloadIssuesMediasFilesSyncStepTask,
  WarehousePushIssuesMediasFilesSyncStepTask,
} from './issues-medias-files.steps';
import { WarehouseDownloadIssuesMediasSyncStepTask } from './issues-medias.steps';
import { WarehouseCleanIssuesSyncStepTask, WarehouseDownloadIssuesSyncStepTask, WarehousePushIssuesSyncStepTask } from './issues.steps';
import { WarehouseCleanLayoutTextsSyncStepTask, WarehouseDownloadLayoutTextsSyncStepTask, WarehousePushLayoutTextsSyncStepTask } from './layout-texts.steps';
import { WarehouseCleanNotesSyncStepTask, WarehouseDownloadNotesSyncStepTask, WarehousePushNotesSyncStepTask } from './notes.steps';
import { WarehouseCleanOutlinesSyncStepTask, WarehouseDownloadOutlinesSyncStepTask, WarehousePushOutlinesSyncStepTask } from './outlines.steps';
import { WarehouseCleanSavedSearchesSyncStepTask, WarehouseDownloadSavedSearchesSyncStepTask, WarehousePushSavedSearchesSyncStepTask } from './saved-searches.steps';
import { WarehouseCleanSystemsSyncStepTask, WarehouseDownloadSystemsSyncStepTask, WarehousePushSystemsSyncStepTask } from './systems.steps';
import { WarehouseCleanUprightsColumnsSyncStepTask, WarehouseDownloadUprightsColumnsSyncStepTask } from './uprights-columns.steps';
import { WarehouseCleanUprightsSyncStepTask, WarehouseDownloadUprightsSyncStepTask } from './uprights.steps';
import { WarehousePushSyncCompletedSyncStepTask } from './sync-completed.steps';
import { WarehouseDownloadSyncStepTask } from './warehouse.steps';
import { WarehousesCleanupSyncStepTask } from './warehouses-cleanup.steps';

@Injectable()
export abstract class WarehouseSyncStepFactory {
  abstract createTasks(step: WarehouseSyncStep, environment: unknown, userPreferences: Preference<unknown>[]): WarehouseSyncStepTask[];
}

@Injectable()
export class DefaultWarehouseSyncStepFactory extends WarehouseSyncStepFactory {
  constructor(private readonly idbService: IndexedService) {
    super();
  }

  createTasks(step: WarehouseSyncStep, environment: unknown, userPreferences: Preference<unknown>[]): WarehouseSyncStepTask[] {
    switch (step) {
      case WAREHOUSE_SYNC_STEP_DOWNLOAD:
        return this.createDownloadTasks(environment, userPreferences);
      case WAREHOUSE_SYNC_STEP_CLEAN:
        return this.createCleanTasks(environment);
      case WAREHOUSE_SYNC_STEP_PUSH:
      default:
        return this.createPushTasks(environment);
    }
  }

  private createPushTasks(environment: unknown): WarehouseSyncStepTask[] {
    return [
      this.createTask(WarehousePushAssetsSyncStepTask, environment),
      this.createTask(WarehousePushInspectionsSyncStepTask, environment),
      this.createTask(WarehousePushIssuesSyncStepTask, environment),
      this.createTask(WarehousePushNotesSyncStepTask, environment),
      this.createTask(WarehousePushSavedSearchesSyncStepTask, environment),
      this.createTask(WarehousePushSystemsSyncStepTask, environment),
      this.createTask(WarehousePushOutlinesSyncStepTask, environment),
      this.createTask(WarehousePushLayoutTextsSyncStepTask, environment),
      this.createTask(WarehousePushIssuesMediasFilesSyncStepTask, environment),
      // Must stay last: it reports what every task above has finished pushing.
      this.createTask(WarehousePushSyncCompletedSyncStepTask, environment),
    ];
  }

  private createDownloadTasks(environment: unknown, userPreferences: Preference<unknown>[]): WarehouseSyncStepTask[] {
    const downloadTasks = [];
    downloadTasks.push(this.createTask(WarehouseDownloadSyncStepTask, environment));
    downloadTasks.push(this.createTask(WarehouseDownloadConfigsSyncStepTask, environment));
    downloadTasks.push(this.createTask(WarehouseDownloadAssetsSyncStepTask, environment));
    downloadTasks.push(this.createTask(WarehouseDownloadIssuesSyncStepTask, environment));
    downloadTasks.push(this.createTask(WarehouseDownloadInspectionsSyncStepTask, environment));
    downloadTasks.push(this.createTask(WarehouseDownloadNotesSyncStepTask, environment));
    downloadTasks.push(this.createTask(WarehouseDownloadSavedSearchesSyncStepTask, environment));
    downloadTasks.push(this.createTask(WarehouseDownloadSystemsSyncStepTask, environment));
    downloadTasks.push(this.createTask(WarehouseDownloadUprightsSyncStepTask, environment));
    downloadTasks.push(this.createTask(WarehouseDownloadOutlinesSyncStepTask, environment));
    downloadTasks.push(this.createTask(WarehouseDownloadLayoutTextsSyncStepTask, environment));
    downloadTasks.push(this.createTask(WarehouseDownloadUprightsColumnsSyncStepTask, environment));
    downloadTasks.push(this.createTask(WarehouseDownloadIssuesMediasSyncStepTask, environment));

    const { appConfig } = environment as any;
    if (appConfig.watson.downloadPicturesOnSync && userPreferences.some((p) => p.key === 'user_downloadPicturesOnSync' && p.value)) {
      const riskLevelFilter = this.findLatestPreferenceValue<PictureSyncRiskFilter[]>(userPreferences, 'user_syncPicturesMinRiskLevel') ?? null;
      downloadTasks.push(new WarehouseDownloadIssuesMediasFilesSyncStepTask(environment, riskLevelFilter));
    }

    return downloadTasks;
  }

  /**
   * Preferences are keyed by string but duplicates can exist (stale docs).
   * `Array.find` would silently return the first — possibly outdated — match,
   * so resolve to the most recently modified one and warn when ambiguous.
   */
  private findLatestPreferenceValue<T>(userPreferences: Preference<unknown>[], key: string): T | undefined {
    const matches = userPreferences.filter((p) => p.key === key);
    if (!matches.length) {
      return undefined;
    }
    if (matches.length > 1) {
      logger.warn('Duplicate preference docs, using the most recently modified', { key, count: matches.length, category: 'sync' });
    }
    const byModified = (p: Preference<unknown>): number => {
      const d = p.audit?.lastModifiedDate;
      if (d instanceof Date) {
        return d.getTime();
      }
      const t = typeof (d as { toMillis?: unknown })?.toMillis === 'function' ? (d as { toMillis: () => number }).toMillis() : new Date(d as unknown as string)?.getTime();
      return Number.isFinite(t) ? t : 0;
    };
    return [...matches].sort((a, b) => byModified(b) - byModified(a))[0]?.value as T;
  }

  private createCleanTasks(environment: unknown): WarehouseSyncStepTask[] {
    return [
      this.createTask(WarehouseCleanAssetsSyncStepTask, environment),
      this.createTask(WarehouseCleanIssuesSyncStepTask, environment),
      this.createTask(WarehouseCleanInspectionsSyncStepTask, environment),
      this.createTask(WarehouseCleanNotesSyncStepTask, environment),
      this.createTask(WarehouseCleanSavedSearchesSyncStepTask, environment),
      this.createTask(WarehouseCleanIssuesMediasFilesSyncStepTask, environment),
      this.createTask(WarehouseCleanSystemsSyncStepTask, environment),
      this.createTask(WarehouseCleanUprightsSyncStepTask, environment),
      this.createTask(WarehouseCleanOutlinesSyncStepTask, environment),
      this.createTask(WarehouseCleanLayoutTextsSyncStepTask, environment),
      this.createTask(WarehouseCleanUprightsColumnsSyncStepTask, environment),
      this.createTask(WarehousesCleanupSyncStepTask, environment),
    ];
  }

  private createTask<T>(taskClass: { new (e: unknown, idb?: IndexedService): T }, environment: unknown): T {
    return new taskClass(environment, this.idbService);
  }
}
